import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { 
  X, 
  Download, 
  ZoomIn, 
  TrendingUp, 
  TrendingDown, 
  Minus,
  Sparkles,
  AlertTriangle,
  CheckCircle,
  Target,
  Lightbulb,
  ArrowRight,
  Loader2,
  RefreshCw,
  Rocket
} from 'lucide-react';
import axios from 'axios';
import { 
  BarChart, Bar, LineChart, Line, AreaChart, Area, ComposedChart, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend 
} from 'recharts';
import { LaunchCampaignButton } from './LaunchCampaignButton';

import { API_BASE_URL as API } from '../utils/apiConfig';

// Indian number formatting
const formatIndianNumber = (num) => {
  if (num === null || num === undefined) return '—';
  const absNum = Math.abs(num);
  if (absNum >= 10000000) return `${(num / 10000000).toFixed(2)} Cr`;
  if (absNum >= 100000) return `${(num / 100000).toFixed(2)} L`;
  if (absNum >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toLocaleString('en-IN');
};

const formatCurrency = (num) => `₹${formatIndianNumber(num)}`;

// Chart colors
const COLORS = ['#8B5CF6', '#EC4899', '#10B981', '#F59E0B', '#3B82F6', '#EF4444', '#06B6D4', '#84CC16'];

export default function ChartZoomModal({ 
  open, 
  onClose, 
  chartType, 
  chartData, 
  chartTitle,
  chartConfig,
  brandId,
  dashboardType
}) {
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(0);
  const [progressText, setProgressText] = useState('Loading...');

  useEffect(() => {
    if (open && chartData && chartData.length > 0) {
      generateChartInsights();
    }
  }, [open, chartData]);

  const generateChartInsights = async () => {
    if (!brandId) {
      setError('Brand context missing — please select a brand to generate insights.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    setProgress(0);
    setProgressText('Analyzing chart data...');
    
    // Progress animation
    const progressInterval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 90) return prev;
        return prev + (prev < 50 ? 6 : 3);
      });
    }, 300);
    
    setTimeout(() => setProgressText('Calculating trends...'), 1000);
    setTimeout(() => setProgressText('Detecting anomalies...'), 2500);
    setTimeout(() => setProgressText('Generating insights...'), 4000);
    
    try {
      // Generate insights based on chart data
      const response = await axios.post(
        `${API}/api/analytics/chart-insights`,
        {
          brand_id: brandId,
          chart_type: chartType,
          chart_title: chartTitle,
          chart_data: chartData,
          dashboard_type: dashboardType
        },
        { 
          withCredentials: true,
          timeout: 45000
        }
      );
      
      clearInterval(progressInterval);
      setProgress(100);
      setInsights(response.data.insights);
    } catch (err) {
      clearInterval(progressInterval);
      console.error('Chart insights error:', err);
      setError('Could not generate AI insight for this chart. Please retry.');
      setInsights(null);
    } finally {
      setLoading(false);
    }
  };

  const generateLocalInsights = () => {
    if (!chartData || chartData.length === 0) return null;
    
    // Analyze chart data locally
    const dataKeys = Object.keys(chartData[0]).filter(k => k !== 'name' && k !== 'month' && k !== 'date' && k !== 'label');
    const primaryKey = dataKeys[0] || 'value';
    
    const values = chartData.map(d => d[primaryKey]).filter(v => typeof v === 'number');
    const sum = values.reduce((a, b) => a + b, 0);
    const avg = sum / values.length;
    const max = Math.max(...values);
    const min = Math.min(...values);
    const latest = values[values.length - 1];
    const previous = values[values.length - 2] || values[0];
    const change = previous ? ((latest - previous) / previous * 100).toFixed(1) : 0;
    const trend = latest > previous ? 'up' : latest < previous ? 'down' : 'stable';
    
    // Find peak and trough
    const peakIndex = values.indexOf(max);
    const troughIndex = values.indexOf(min);
    const peakLabel = chartData[peakIndex]?.name || chartData[peakIndex]?.month || `Point ${peakIndex + 1}`;
    const troughLabel = chartData[troughIndex]?.name || chartData[troughIndex]?.month || `Point ${troughIndex + 1}`;
    
    // Calculate volatility
    const variance = values.reduce((acc, val) => acc + Math.pow(val - avg, 2), 0) / values.length;
    const stdDev = Math.sqrt(variance);
    const volatility = (stdDev / avg * 100).toFixed(1);
    
    return {
      description: {
        title: "What the chart shows",
        content: `This ${chartType} visualization displays ${chartTitle} across ${chartData.length} data points. The data ranges from ${formatIndianNumber(min)} to ${formatIndianNumber(max)}, with an average of ${formatIndianNumber(avg)}.`
      },
      analysis: {
        title: "Trend Analysis",
        content: `Current value stands at ${formatIndianNumber(latest)}, representing a ${Math.abs(change)}% ${trend === 'up' ? 'increase' : trend === 'down' ? 'decrease' : 'no change'} from the previous period. Peak performance was observed at ${peakLabel} (${formatIndianNumber(max)}), while the lowest point was at ${troughLabel} (${formatIndianNumber(min)}). Volatility coefficient: ${volatility}%.`,
        trend: trend,
        change: parseFloat(change)
      },
      insights: {
        title: "Key Insights",
        points: [
          {
            type: trend === 'up' ? 'positive' : trend === 'down' ? 'negative' : 'neutral',
            text: trend === 'up' 
              ? `Strong growth momentum with ${change}% improvement indicates effective strategy execution`
              : trend === 'down'
              ? `Declining trend of ${Math.abs(change)}% requires immediate attention and root cause analysis`
              : `Stable performance maintaining consistent levels - opportunity for growth acceleration`
          },
          {
            type: volatility > 20 ? 'warning' : 'positive',
            text: volatility > 20 
              ? `High volatility (${volatility}%) suggests inconsistent performance - need for stabilization measures`
              : `Low volatility (${volatility}%) indicates predictable, stable operations`
          },
          {
            type: 'info',
            text: `Performance spread of ${formatIndianNumber(max - min)} between peak and trough represents ${((max - min) / avg * 100).toFixed(1)}% variance from mean`
          }
        ]
      },
      recommendations: {
        title: "Recommended Actions",
        actions: [
          {
            priority: 'P0',
            action: trend === 'down' 
              ? `Investigate root cause of ${Math.abs(change)}% decline - conduct segment-level analysis`
              : `Capitalize on ${change}% growth by scaling successful initiatives`,
            owner: 'Analytics Team',
            impact: `Potential ${trend === 'down' ? 'recovery' : 'additional'} impact of ${formatIndianNumber(avg * 0.1)}`
          },
          {
            priority: 'P1',
            action: volatility > 15 
              ? 'Implement variance reduction strategies through process standardization'
              : 'Explore growth acceleration opportunities in top-performing segments',
            owner: 'Operations',
            impact: `Stabilization/growth potential of ${volatility > 15 ? (volatility - 10).toFixed(0) : 10}%`
          },
          {
            priority: 'P2',
            action: `Set up automated monitoring alerts for ${chartTitle} with threshold at ${formatIndianNumber(avg * 0.9)} (10% below average)`,
            owner: 'Data Team',
            impact: 'Early warning system for performance degradation'
          }
        ]
      },
      statistics: {
        current: latest,
        previous: previous,
        change: parseFloat(change),
        trend: trend,
        average: avg,
        max: max,
        min: min,
        volatility: parseFloat(volatility),
        dataPoints: chartData.length
      }
    };
  };

  const renderChart = () => {
    if (!chartData || chartData.length === 0) {
      return <div className="flex items-center justify-center h-80 text-muted-foreground">No data available</div>;
    }

    const config = chartConfig || {};
    const dataKeys = Object.keys(chartData[0]).filter(k => k !== 'name' && k !== 'month' && k !== 'date' && k !== 'label');

    switch (chartType) {
      case 'bar':
        return (
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis 
                dataKey={config.xKey || "name"} 
                tick={{ fontSize: 11 }}
                angle={-45}
                textAnchor="end"
                height={80}
              />
              <YAxis tickFormatter={(v) => formatIndianNumber(v)} tick={{ fontSize: 11 }} />
              <Tooltip 
                formatter={(value, name) => [formatIndianNumber(value), name]}
                contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }}
              />
              <Legend />
              {dataKeys.map((key, idx) => (
                <Bar key={key} dataKey={key} fill={COLORS[idx % COLORS.length]} radius={[4, 4, 0, 0]} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        );

      case 'line':
        return (
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis 
                dataKey={config.xKey || "name"} 
                tick={{ fontSize: 11 }}
                angle={-45}
                textAnchor="end"
                height={80}
              />
              <YAxis tickFormatter={(v) => formatIndianNumber(v)} tick={{ fontSize: 11 }} />
              <Tooltip 
                formatter={(value, name) => [formatIndianNumber(value), name]}
                contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }}
              />
              <Legend />
              {dataKeys.map((key, idx) => (
                <Line 
                  key={key} 
                  type="monotone" 
                  dataKey={key} 
                  stroke={COLORS[idx % COLORS.length]} 
                  strokeWidth={2}
                  dot={{ fill: COLORS[idx % COLORS.length], strokeWidth: 2, r: 4 }}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        );

      case 'area':
        return (
          <ResponsiveContainer width="100%" height={400}>
            <AreaChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis 
                dataKey={config.xKey || "name"} 
                tick={{ fontSize: 11 }}
                angle={-45}
                textAnchor="end"
                height={80}
              />
              <YAxis tickFormatter={(v) => formatIndianNumber(v)} tick={{ fontSize: 11 }} />
              <Tooltip 
                formatter={(value, name) => [formatIndianNumber(value), name]}
                contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }}
              />
              <Legend />
              {dataKeys.map((key, idx) => (
                <Area 
                  key={key} 
                  type="monotone" 
                  dataKey={key} 
                  fill={COLORS[idx % COLORS.length]} 
                  fillOpacity={0.3}
                  stroke={COLORS[idx % COLORS.length]} 
                  strokeWidth={2}
                />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        );

      case 'pie':
        return (
          <ResponsiveContainer width="100%" height={400}>
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                labelLine={true}
                label={({ name, value, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                outerRadius={150}
                fill="#8884d8"
                dataKey={dataKeys[0] || "value"}
              >
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip 
                formatter={(value, name) => [formatIndianNumber(value), name]}
                contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }}
              />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        );

      case 'composed':
      default:
        return (
          <ResponsiveContainer width="100%" height={400}>
            <ComposedChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis 
                dataKey={config.xKey || "name"} 
                tick={{ fontSize: 11 }}
                angle={-45}
                textAnchor="end"
                height={80}
              />
              <YAxis tickFormatter={(v) => formatIndianNumber(v)} tick={{ fontSize: 11 }} />
              <Tooltip 
                formatter={(value, name) => [formatIndianNumber(value), name]}
                contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }}
              />
              <Legend />
              {dataKeys.map((key, idx) => (
                idx % 2 === 0 ? (
                  <Bar key={key} dataKey={key} fill={COLORS[idx % COLORS.length]} radius={[4, 4, 0, 0]} />
                ) : (
                  <Line key={key} type="monotone" dataKey={key} stroke={COLORS[idx % COLORS.length]} strokeWidth={2} />
                )
              ))}
            </ComposedChart>
          </ResponsiveContainer>
        );
    }
  };

  const getTrendIcon = (trend) => {
    if (trend === 'up') return <TrendingUp className="w-4 h-4 text-emerald-400" />;
    if (trend === 'down') return <TrendingDown className="w-4 h-4 text-red-400" />;
    return <Minus className="w-4 h-4 text-gray-400" />;
  };

  const getInsightIcon = (type) => {
    if (type === 'positive') return <CheckCircle className="w-4 h-4 text-emerald-400" />;
    if (type === 'negative') return <AlertTriangle className="w-4 h-4 text-red-400" />;
    if (type === 'warning') return <AlertTriangle className="w-4 h-4 text-amber-400" />;
    return <Lightbulb className="w-4 h-4 text-blue-400" />;
  };

  const getPriorityColor = (priority) => {
    if (priority === 'P0') return 'bg-red-500';
    if (priority === 'P1') return 'bg-amber-500';
    return 'bg-blue-500';
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto bg-background">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-violet-500/20">
                <ZoomIn className="w-5 h-5 text-violet-400" />
              </div>
              <div>
                <DialogTitle className="text-xl">{chartTitle}</DialogTitle>
                <p className="text-sm text-muted-foreground mt-1">Detailed Analysis View</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={generateChartInsights}
                disabled={loading}
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh Insights
              </Button>
            </div>
          </div>
        </DialogHeader>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-4">
          {/* Enlarged Chart */}
          <Card className="lg:col-span-2">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <ZoomIn className="w-4 h-4 text-violet-400" />
                Chart Visualization
              </CardTitle>
            </CardHeader>
            <CardContent>
              {renderChart()}
            </CardContent>
          </Card>

          {/* AI Insights */}
          {loading ? (
            <Card className="lg:col-span-2">
              <CardContent className="p-8 flex items-center justify-center">
                <div className="flex flex-col items-center gap-4 w-full max-w-md">
                  <Sparkles className="w-8 h-8 text-violet-400 animate-pulse" />
                  <p className="text-muted-foreground">{progressText}</p>
                  <div className="w-full">
                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-violet-500 to-purple-500 rounded-full transition-all duration-300"
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                    <p className="text-xs text-muted-foreground text-center mt-2">{progress}%</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : insights ? (
            <>
              {/* Description */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Target className="w-4 h-4 text-blue-400" />
                    {insights.description?.title || "Description"}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">{insights.description?.content}</p>
                </CardContent>
              </Card>

              {/* Trend Analysis */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    {getTrendIcon(insights.analysis?.trend)}
                    {insights.analysis?.title || "Analysis"}
                    <Badge className={`ml-auto ${
                      insights.analysis?.trend === 'up' ? 'bg-emerald-500' : 
                      insights.analysis?.trend === 'down' ? 'bg-red-500' : 'bg-gray-500'
                    }`}>
                      {insights.analysis?.change > 0 ? '+' : ''}{insights.analysis?.change}%
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">{insights.analysis?.content}</p>
                </CardContent>
              </Card>

              {/* Key Insights */}
              <Card className="lg:col-span-2">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-violet-400" />
                    {insights.insights?.title || "Key Insights"}
                    {insights.ai_powered && (
                      <Badge variant="outline" className="ml-auto text-xs border-violet-500 text-violet-500">
                        AI-Powered
                      </Badge>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {/* Key Finding Highlight */}
                  {insights.key_finding && (
                    <div className="p-4 mb-4 rounded-lg bg-gradient-to-r from-violet-500/20 to-purple-500/10 border border-violet-500/30">
                      <div className="flex items-start gap-3">
                        <Sparkles className="w-5 h-5 text-violet-400 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm font-semibold text-violet-300 mb-1">Key Finding</p>
                          <p className="text-sm">{insights.key_finding}</p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div className="space-y-3">
                    {insights.insights?.points?.map((point, idx) => (
                      <div key={idx} className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
                        {getInsightIcon(point.type)}
                        <p className="text-sm">
                          {typeof point.text === 'string' 
                            ? point.text 
                            : point.text?.insight || point.text?.content || (typeof point.text === 'object' ? JSON.stringify(point.text) : point.text)}
                        </p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
              
              {/* Anomalies & Predictions Row */}
              {(insights.anomalies?.detected?.length > 0 || insights.prediction) && (
                <>
                  {/* Anomaly Detection */}
                  {insights.anomalies?.detected?.length > 0 && (
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <AlertTriangle className="w-4 h-4 text-amber-400" />
                          Anomaly Detection
                          <Badge variant="outline" className="ml-auto text-amber-500 border-amber-500">
                            {insights.anomalies.detected.length} found
                          </Badge>
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-muted-foreground mb-3">
                          {typeof insights.anomalies.analysis === 'string' 
                            ? insights.anomalies.analysis 
                            : insights.anomalies.analysis?.content || JSON.stringify(insights.anomalies.analysis)}
                        </p>
                        <div className="space-y-2">
                          {insights.anomalies.detected.slice(0, 3).map((anomaly, idx) => (
                            <div key={idx} className={`p-2 rounded-lg text-sm flex items-center justify-between ${
                              anomaly.type === 'spike' ? 'bg-emerald-500/10 border border-emerald-500/30' : 'bg-red-500/10 border border-red-500/30'
                            }`}>
                              <span>
                                {typeof anomaly.label === 'string' 
                                  ? anomaly.label 
                                  : anomaly.label?.month || anomaly.label?.name || JSON.stringify(anomaly.label)}
                              </span>
                              <Badge className={anomaly.type === 'spike' ? 'bg-emerald-500' : 'bg-red-500'}>
                                {anomaly.type === 'spike' ? '↑' : '↓'} {formatIndianNumber(anomaly.value)}
                              </Badge>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                  
                  {/* Prediction */}
                  {insights.prediction && (
                    <Card className="bg-gradient-to-br from-blue-500/10 to-cyan-500/5">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <Target className="w-4 h-4 text-blue-400" />
                          Forecast & Prediction
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm">
                          {typeof insights.prediction === 'string' 
                            ? insights.prediction 
                            : insights.prediction?.insight || insights.prediction?.content || JSON.stringify(insights.prediction)}
                        </p>
                        {insights.confidence_score && (
                          <div className="mt-3 flex items-center gap-2">
                            <span className="text-xs text-muted-foreground">Confidence:</span>
                            <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-blue-500 rounded-full"
                                style={{ width: `${insights.confidence_score * 100}%` }}
                              />
                            </div>
                            <span className="text-xs font-medium">{(insights.confidence_score * 100).toFixed(0)}%</span>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  )}
                </>
              )}
              
              {/* Root Cause Analysis */}
              {insights.root_cause && (
                <Card className="lg:col-span-2 bg-gradient-to-br from-amber-500/10 to-orange-500/5">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Lightbulb className="w-4 h-4 text-amber-400" />
                      Root Cause Hypothesis
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm">{insights.root_cause}</p>
                  </CardContent>
                </Card>
              )}

              {/* Recommendations */}
              <Card className="lg:col-span-2">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Lightbulb className="w-4 h-4 text-amber-400" />
                    {insights.recommendations?.title || "Recommended Actions"}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {insights.recommendations?.actions?.map((action, idx) => (
                      <div key={idx} className="flex items-start gap-3 p-3 rounded-lg border">
                        <Badge className={`${getPriorityColor(action.priority)} text-white text-xs`}>
                          {action.priority}
                        </Badge>
                        <div className="flex-1">
                          <p className="text-sm font-medium">{action.action}</p>
                          <div className="flex items-center gap-4 mt-1 text-xs text-muted-foreground">
                            <span>Owner: {action.owner}</span>
                            <span>Impact: {action.expected_impact || action.impact}</span>
                          </div>
                        </div>
                        <LaunchCampaignButton
                          brandId={brandId}
                          segmentName={`${chartTitle} - ${action.action?.substring(0, 30) || 'AI Recommendation'}`}
                          recommendation={{
                            action: action.action,
                            priority: action.priority,
                            impact: action.expected_impact || action.impact
                          }}
                          variant="outline"
                          size="sm"
                        />
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Statistics Summary */}
              {insights.statistics && (
                <Card className="lg:col-span-2">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Statistical Summary</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                      <div className="p-3 rounded-lg bg-violet-500/10">
                        <p className="text-xs text-muted-foreground">Current</p>
                        <p className="text-lg font-semibold text-violet-400">{formatIndianNumber(insights.statistics.current)}</p>
                      </div>
                      <div className="p-3 rounded-lg bg-blue-500/10">
                        <p className="text-xs text-muted-foreground">Previous</p>
                        <p className="text-lg font-semibold text-blue-400">{formatIndianNumber(insights.statistics.previous)}</p>
                      </div>
                      <div className="p-3 rounded-lg bg-emerald-500/10">
                        <p className="text-xs text-muted-foreground">Average</p>
                        <p className="text-lg font-semibold text-emerald-400">{formatIndianNumber(insights.statistics.average)}</p>
                      </div>
                      <div className="p-3 rounded-lg bg-pink-500/10">
                        <p className="text-xs text-muted-foreground">Maximum</p>
                        <p className="text-lg font-semibold text-pink-400">{formatIndianNumber(insights.statistics.max)}</p>
                      </div>
                      <div className="p-3 rounded-lg bg-amber-500/10">
                        <p className="text-xs text-muted-foreground">Minimum</p>
                        <p className="text-lg font-semibold text-amber-400">{formatIndianNumber(insights.statistics.min)}</p>
                      </div>
                      <div className="p-3 rounded-lg bg-cyan-500/10">
                        <p className="text-xs text-muted-foreground">Volatility</p>
                        <p className="text-lg font-semibold text-cyan-400">{insights.statistics.volatility}%</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Export the zoom button component for easy use in dashboards
export function ChartZoomButton({ onClick, className = "" }) {
  return (
    <Button 
      variant="ghost" 
      size="icon"
      onClick={onClick}
      className={`h-8 w-8 hover:bg-violet-500/20 ${className}`}
      title="Expand chart with AI insights"
    >
      <ZoomIn className="w-4 h-4 text-violet-400" />
    </Button>
  );
}
